import { useEffect, useCallback, useState, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { AppDispatch, StoreState } from '../../../../store';
import { useDispatch, useSelector } from 'react-redux';
import { propertySelectors } from '../../../../ducks/property';
import { propertyActions } from '../../../../ducks/property/actions';
import { CommercialListRequestBody } from '../../../../core/api/request/property/commercial/types';

export const useCommercialPage = () => {
    const dispatch = useDispatch<AppDispatch>();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const commercial = useSelector((state: StoreState) => propertySelectors.commercials(state));
    const isUpdatingUrlRef = useRef(false);
    const lastProcessedUrlRef = useRef(location.search);

    // Parse filters from URL search params
    const parseFiltersFromUrl = useCallback((params: URLSearchParams): CommercialListRequestBody => {
        const filters: CommercialListRequestBody = {};
        const status = params.get('status');
        const type = params.get('type');
        const district = params.get('district');
        const commune = params.get('commune');
        const popularPlace = params.get('popularPlace');
        const bedrooms = params.get('bedrooms');
        const priceMin = params.get('priceMin');
        const priceMax = params.get('priceMax');
        const property_code = params.get('property_code');

        if (status) filters.status = status;
        if (type) filters.type = type;
        if (district) filters.district = district;
        if (commune) filters.commune = commune;
        if (popularPlace) filters.popularPlace = popularPlace;
        if (bedrooms) filters.bedrooms = bedrooms;
        if (priceMin) filters.priceMin = priceMin;
        if (priceMax) filters.priceMax = priceMax;
        if (property_code) filters.property_code = property_code;

        return filters;
    }, []);

    // Pagination and filter state
    const [currentPage, setCurrentPage] = useState(() => {
        const page = searchParams.get('page');
        return page ? parseInt(page, 10) : 1;
    });
    const [currentFilters, setCurrentFilters] = useState<CommercialListRequestBody>(() => {
        return parseFiltersFromUrl(searchParams);
    });
    const [initialSearchApplied, setInitialSearchApplied] = useState(false);
    const pageSize = 9; // Fixed page size

    // Read from URL when it changes externally (e.g., browser back/forward or navigation back from detail)
    useEffect(() => {
        if (isUpdatingUrlRef.current || location.search === lastProcessedUrlRef.current) {
            return;
        }

        lastProcessedUrlRef.current = location.search;

        const pageFromUrl = searchParams.get('page');
        const newPage = pageFromUrl ? parseInt(pageFromUrl, 10) : 1;
        const newFilters = parseFiltersFromUrl(searchParams);

        setCurrentPage(prevPage => {
            if (newPage !== prevPage) {
                return newPage;
            }
            return prevPage;
        });

        setCurrentFilters(prevFilters => {
            const filtersChanged = JSON.stringify(newFilters) !== JSON.stringify(prevFilters);
            if (filtersChanged) {
                return newFilters;
            }
            return prevFilters;
        });
    }, [location.search, searchParams, parseFiltersFromUrl]);

    // Sync page and filters to URL search params when state changes
    useEffect(() => {
        if (isUpdatingUrlRef.current) {
            isUpdatingUrlRef.current = false;
            return;
        }

        const params = new URLSearchParams();
        if (currentPage > 1) {
            params.set('page', currentPage.toString());
        }

        Object.entries(currentFilters).forEach(([key, value]) => {
            if (value) {
                params.set(key, value.toString());
            }
        });

        const newSearch = params.toString();
        const currentSearch = location.search.substring(1);

        if (newSearch !== currentSearch) {
            isUpdatingUrlRef.current = true;
            lastProcessedUrlRef.current = `?${newSearch}`;
            setSearchParams(params, { replace: true });
        }
    }, [currentPage, currentFilters, location.search, setSearchParams, parseFiltersFromUrl]);

    // Check if there's search data from home page navigation
    useEffect(() => {
        const state = location.state as { searchData?: any; fromHome?: boolean };
        if (state?.fromHome && state?.searchData && !initialSearchApplied) {
            const searchData = state.searchData;

            // Convert search form data to API parameters
            const searchParams = {
                status: searchData.propertyStatus,
                type: searchData.propertyType,
                district: searchData.district,
                commune: searchData.commune,
                popularPlace: searchData.popularPlace,
                bedrooms: searchData.bedrooms,
                priceMin: searchData.minPrice,
                priceMax: searchData.maxPrice,
                property_code: searchData.propertyCode,
            };

            // Filter out empty values
            const filteredParams = Object.entries(searchParams).reduce((acc, [key, value]) => {
                if (value !== '' && value !== null && value !== undefined) {
                    acc[key as keyof CommercialListRequestBody] = typeof value === 'number' ? value.toString() : value;
                }
                return acc;
            }, {} as CommercialListRequestBody);

            setCurrentFilters(filteredParams);
            setCurrentPage(1);
            setInitialSearchApplied(true);
        }
    }, [location.state, initialSearchApplied]);

    useEffect(() => {
        // Don't make initial API call if we're waiting for search data from navigation
        const state = location.state as { searchData?: any; fromHome?: boolean };
        if (state?.fromHome && state?.searchData && !initialSearchApplied) {
            // Skip initial call, wait for search data to be processed
            return;
        }

        // Load data with current filters and pagination
        const requestParams = {
            ...currentFilters,
            page: currentPage,
            page_size: pageSize
        };

        dispatch(propertyActions.getCommercialList.started(requestParams));
    }, [dispatch, currentPage, currentFilters, initialSearchApplied, location.state]);

    const searchProperties = useCallback((searchParams: CommercialListRequestBody) => {
        // Filter out empty values and convert numbers to strings
        const filteredParams = Object.entries(searchParams).reduce((acc, [key, value]) => {
            if (value !== '' && value !== null && value !== undefined) {
                acc[key as keyof CommercialListRequestBody] = typeof value === 'number' ? value.toString() : value;
            }
            return acc;
        }, {} as CommercialListRequestBody);

        // Remove page and page_size from filters to avoid conflicts
        delete filteredParams.page;
        delete filteredParams.page_size;

        // Store current filters and reset to page 1 when searching
        setCurrentFilters(filteredParams);
        setCurrentPage(1);
    }, []);

    // Pagination handlers
    const handlePageChange = useCallback((newPage: number) => {
        setCurrentPage(newPage);
    }, []);

    const handlePrevious = useCallback(() => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    }, [currentPage]);

    const handleNext = useCallback(() => {
        if (commercial && currentPage < commercial.total_pages) {
            setCurrentPage(currentPage + 1);
        }
    }, [currentPage, commercial]);

    const properties = commercial?.data || [];
    const loading = !commercial;
    const totalPages = commercial?.total_pages || 0;
    const totalItems = commercial?.total_items || 0;

    // Get original search data for form population
    const originalSearchData = location.state as { searchData?: any; fromHome?: boolean };

    // Convert currentFilters (API params) back to SearchForm initialValues format
    const getFormInitialValues = useCallback(() => {
        // Start with originalSearchData if it exists (from home page navigation)
        const baseValues = originalSearchData?.searchData || {};

        // Merge: prioritize URL params over originalSearchData, but keep originalSearchData if URL param is missing
        return {
            propertyStatus: currentFilters.status || baseValues.propertyStatus || '',
            propertyType: currentFilters.type || baseValues.propertyType || '',
            district: currentFilters.district || baseValues.district || '',
            commune: currentFilters.commune || baseValues.commune || '',
            popularPlace: currentFilters.popularPlace || baseValues.popularPlace || '',
            bedrooms: currentFilters.bedrooms || baseValues.bedrooms || '',
            minPrice: currentFilters.priceMin || baseValues.minPrice || '',
            maxPrice: currentFilters.priceMax || baseValues.maxPrice || '',
            propertyCode: currentFilters.property_code || baseValues.propertyCode || '',
        };
    }, [currentFilters, originalSearchData]);

    // Get current URL search string for navigation
    const getCurrentSearchString = useCallback(() => {
        return location.search;
    }, [location.search]);

    return {
        properties,
        loading,
        searchProperties,
        currentFilters,
        originalSearchData: originalSearchData?.searchData,
        formInitialValues: getFormInitialValues(),
        // Pagination
        currentPage,
        pageSize,
        totalPages,
        totalItems,
        handlePageChange,
        handlePrevious,
        handleNext,
        getCurrentSearchString,
    };
}; 